import { ChangeEvent, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { FormApi } from 'final-form';
import { useFormState } from 'react-final-form';
import { v4 as uuidv4 } from 'uuid';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { DONT_HAVE_INTERNET_CONNECTION } from 'CorePackage/MainModule/constants/notices';
import { MB } from 'CorePackage/MainModule/constants/fileSize';
import { getMaxFileSize } from 'CorePackage/MainModule/common/getMaxFileSize';
import { addNoticeKnmFileSizeErrorDetailed } from 'CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorSmall';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Button } from 'UIPackage/MainModule/components/Button';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { getKnmSystemType } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';

import { ISmevRequestForm } from '../interfaces/ISmevRequestForm';
import styles from './SmevAttachments.module.scss';

interface ISmevAttachmentsProps {
  disabled?: boolean;
  form: FormApi<ISmevRequestForm, Partial<ISmevRequestForm>>;
}

export const SmevAttachments = ({ disabled, form }: ISmevAttachmentsProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const inputRef = useRef<HTMLInputElement>(null);
  const { uploadKnmDocumentAttachment, downloadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const settings = useSelector(getAdminFetchSettings);
  const knmSystemType = useSelector(getKnmSystemType);
  const { values } = useFormState<ISmevRequestForm>({ subscription: { values: true } });
  const attachments = values.attachments || [];

  const maxFileSize = getMaxFileSize(settings, knmSystemType);

  const uploadSingleFile = useCallback(
    async (file: File) => {
      const listId = uuidv4();
      const currentAttachments = form.getState().values.attachments || [];

      form.change('attachments', [
        ...currentAttachments,
        {
          fileName: file.name,
          fileSize: file.size,
          isNew: true,
          listId,
          file,
          signatureInfo: null,
        },
      ]);

      try {
        const uploadedAttachment = await uploadKnmDocumentAttachment(file);
        const attachmentsAfterUpload = form.getState().values.attachments || [];
        const uploadedIndex = attachmentsAfterUpload.findIndex(item => item.listId === listId);

        if (uploadedIndex < 0) {
          return;
        }

        const nextAttachments = [...attachmentsAfterUpload];

        nextAttachments[uploadedIndex] = {
          ...uploadedAttachment,
          isNew: true,
          listId,
        };

        form.change('attachments', nextAttachments);
      } catch (error) {
        const attachmentsAfterError = form.getState().values.attachments || [];

        form.change(
          'attachments',
          attachmentsAfterError.filter(item => item.listId !== listId),
        );

        dispatch(
          addNoticeAction({
            renderContent: DONT_HAVE_INTERNET_CONNECTION,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
          }),
        );
      }
    },
    [dispatch, form, uploadKnmDocumentAttachment],
  );

  const handleSelectFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      e.target.value = '';

      if (!file) {
        return;
      }

      if (maxFileSize && file.size > maxFileSize * MB) {
        dispatch(addNoticeKnmFileSizeErrorDetailed(maxFileSize));

        return;
      }

      await uploadSingleFile(file);
    },
    [dispatch, maxFileSize, uploadSingleFile],
  );

  const handleRemove = useCallback(
    (index: number) => () => {
      const currentAttachments = form.getState().values.attachments || [];

      form.change(
        'attachments',
        currentAttachments.filter((_, attachmentIndex) => attachmentIndex !== index),
      );
    },
    [form],
  );

  const handleDownload = useCallback(
    (attachment: IKnmFormAttachment) => () => {
      if (!attachment.guid || !attachment.filePath) {
        return;
      }

      downloadKnmDocumentAttachment({
        fileName: attachment.fileName || null,
        filePath: attachment.filePath,
        fileSize: attachment.fileSize,
        mimeType: attachment.mimeType || null,
        bucketName: attachment.bucketName || undefined,
      });
    },
    [downloadKnmDocumentAttachment],
  );

  return (
    <FieldBlock
      label="Файлы"
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          id="smevAttachmentsAdd"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        />
      }
    >
      <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={handleSelectFile} />
      {attachments.length > 0 && (
        <ul className={styles.AttachmentsList}>
          {attachments.map((attachment, index) => {
            let progress: number | undefined;

            if (attachment.isNew) {
              progress = attachment.dateCreate ? 100 : 0;
            }

            return (
              <li className={styles.Attachment} key={attachment.listId || attachment.guid || index}>
                <div className={styles.AttachmentRow}>
                  <UploadFile
                    className={styles.File}
                    fileName={attachment.fileName || ''}
                    fileSize={attachment.fileSize}
                    fileType={attachment.mimeType || undefined}
                    progress={progress}
                    onFileNameClick={attachment.guid && attachment.filePath ? handleDownload(attachment) : undefined}
                    onRemove={disabled ? undefined : handleRemove(index)}
                  />
                  {attachment.guid && <span className={styles.Guid}>guid = {attachment.guid}</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </FieldBlock>
  );
};
